import React, { useState, useRef } from 'react';
import { Upload, CheckCircle2, XCircle, AlertCircle, FileArchive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminBulkImageUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      toast.error('Bitte eine ZIP-Datei auswählen');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/admin/panasonic/products/bulk-image-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResult(res.data);
      toast.success(res.data.message || 'Upload abgeschlossen');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
          <FileArchive className="h-6 w-6 text-primary" />
          Bulk-Bilder-Upload
        </h2>
        <p className="text-sm text-gray-600">
          Lade eine ZIP-Datei mit Produktbildern hoch. Die Bilder werden automatisch
          anhand des Dateinamens der SKU zugeordnet.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-testid="bulk-upload-info">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-2">So funktioniert es:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Dateinamen müssen die SKU enthalten, z.B. <code className="bg-white px-1 rounded">TV-77Z95B.jpg</code> für Produkt <code className="bg-white px-1 rounded">TV-77Z95B</code></li>
              <li>Unterstützte Formate: JPG, JPEG, PNG, WEBP</li>
              <li>Groß-/Kleinschreibung wird ignoriert, Unterstriche und Bindestriche sind austauschbar</li>
              <li>Nicht zugeordnete Dateien werden separat aufgelistet — bereits vorhandene Bilder werden überschrieben</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
        <FileArchive className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip"
          onChange={handleFileChange}
          className="hidden"
          id="bulk-zip-input"
          data-testid="bulk-zip-file-input"
        />
        <label htmlFor="bulk-zip-input" className="cursor-pointer">
          <span className="text-primary font-medium hover:underline">
            ZIP-Datei auswählen
          </span>
          <span className="text-gray-500"> oder hierher ziehen</span>
        </label>
        {file && (
          <div className="mt-4 text-sm text-gray-700" data-testid="selected-file">
            <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}
      </div>

      <Button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full sm:w-auto"
        data-testid="bulk-upload-submit"
      >
        <Upload className="h-4 w-4 me-2" />
        {uploading ? 'Wird hochgeladen...' : 'Bilder hochladen und zuordnen'}
      </Button>

      {result && (
        <div className="mt-8 space-y-4" data-testid="bulk-upload-result">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="font-semibold text-green-900">Zugeordnet</p>
              </div>
              <p className="text-3xl font-bold text-green-700 mt-1">{result.matched_count}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <p className="font-semibold text-yellow-900">Kein Treffer</p>
              </div>
              <p className="text-3xl font-bold text-yellow-700 mt-1">{result.unmatched_count}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <p className="font-semibold text-red-900">Fehler</p>
              </div>
              <p className="text-3xl font-bold text-red-700 mt-1">{result.error_count}</p>
            </div>
          </div>

          {result.matched?.length > 0 && (
            <details className="bg-white border border-border rounded-lg p-4" open>
              <summary className="cursor-pointer font-medium">
                Erfolgreich zugeordnete Bilder ({result.matched.length})
              </summary>
              <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
                {result.matched.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                    <span className="text-gray-600">{m.file}</span>
                    <span className="font-medium text-green-700">→ {m.sku}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {result.unmatched?.length > 0 && (
            <details className="bg-white border border-border rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-yellow-800">
                Ohne Treffer ({result.unmatched.length}) — Dateinamen prüfen
              </summary>
              <ul className="mt-3 text-sm space-y-1 list-disc list-inside">
                {result.unmatched.map((f, i) => (
                  <li key={i} className="text-gray-600">{f}</li>
                ))}
              </ul>
            </details>
          )}

          {result.errors?.length > 0 && (
            <details className="bg-white border border-border rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-red-800">
                Fehler ({result.errors.length})
              </summary>
              <ul className="mt-3 text-sm space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i} className="text-gray-600">
                    <strong>{e.file}</strong>: {e.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBulkImageUpload;
